// infcode_change - new file
import { MessageV2 } from "@/session/message-v2"
import { asRecord, parseJsonish, sanitizeToolName, stringifyToolOutput } from "./util"

type LegacyStatus = "completed" | "error"

export type LegacyToolCall = {
  id?: string
  callID?: string
  toolCallId?: string
  tool?: string
  name?: string
  toolName?: string
  toolCall?: {
    id?: string
    type?: string
    function?: {
      name?: string
      arguments?: unknown
    }
  }
  status?: string
  parsedArgs?: unknown
  args?: unknown
  input?: unknown
  parameters?: unknown
  output?: unknown
  error?: unknown
  startTime?: unknown
  endTime?: unknown
}

export type LegacyToolMapInput = {
  sessionID: MessageV2.ToolPart["sessionID"]
  messageID: MessageV2.ToolPart["messageID"]
  partID: MessageV2.ToolPart["id"]
  created: number
  directory: string
  toolCall: LegacyToolCall
  output?: unknown
}

type ToolMapping = {
  tool: string
  input: Record<string, unknown>
  metadata?: Record<string, unknown>
}

type LegacyMetadata = {
  toolName: string
  status: string
  parsedArgs: Record<string, unknown>
  toolCallId: string
}

const completedStatuses = new Set(["done", "completed", "success"])
const approvedStatuses = new Set(["approved"])
const unfinishedStatuses = new Set(["generating", "generated", "calling", "running", "pending"])

export function mapLegacyToolCall(input: LegacyToolMapInput): MessageV2.ToolPart | undefined {
  if (toolName(input.toolCall) === "ask_user_question") return undefined
  return toolPart(input)
}

export function mapLegacyToolCallParts(input: LegacyToolMapInput): MessageV2.Part[] {
  if (toolName(input.toolCall) === "ask_user_question") return questionParts(input)
  const part = toolPart(input)
  return part ? [part] : []
}

function toolPart(input: LegacyToolMapInput): MessageV2.ToolPart {
  const rawToolName = toolName(input.toolCall)
  const args = parsedArgs(input.toolCall)
  const status = input.toolCall.status ?? "unknown"
  const callID = toolCallID(input.toolCall)
  const mapping = mapTool(rawToolName, args, input.directory, isCompletedStatus(status))
  const legacy: LegacyMetadata = {
    toolName: rawToolName,
    status,
    parsedArgs: args,
    toolCallId: callID,
  }
  const metadata = {
    ...(mapping.metadata ?? {}),
    legacy,
  }
  const toolTime = {
    start: time(input.toolCall.startTime, input.created),
    end: time(input.toolCall.endTime, input.created),
  }
  const output = input.output ?? input.toolCall.output
  const finalStatus = terminalStatus(status)

  if (finalStatus === "completed") {
    return {
      id: input.partID,
      sessionID: input.sessionID,
      messageID: input.messageID,
      type: "tool",
      callID,
      tool: mapping.tool,
      state: {
        status: "completed",
        input: mapping.input,
        output: stringifyToolOutput(output, syntheticCompletedOutput(status)),
        title: title(mapping.tool, mapping.input),
        metadata,
        time: toolTime,
      },
    }
  }

  return {
    id: input.partID,
    sessionID: input.sessionID,
    messageID: input.messageID,
    type: "tool",
    callID,
    tool: mapping.tool,
    state: {
      status: "error",
      input: mapping.input,
      error: errorText(status, input.toolCall.error ?? output),
      metadata,
      time: toolTime,
    },
  }
}

function mapTool(
  rawToolName: string,
  args: Record<string, unknown>,
  directory: string,
  completed: boolean,
): ToolMapping {
  switch (rawToolName) {
    case "read_file":
      if (!completed) return legacy(rawToolName, args)
      return { tool: "read", input: compact({ filePath: stringArg(args, "filePath", "filepath", "file"), offset: args.offset, limit: args.limit }) }
    case "list_files":
      return {
        tool: "read",
        input: compact({ filePath: stringArg(args, "dirPath", "path", "directory", "filePath") }),
        metadata: compact({ recursive: args.recursive }),
      }
    case "run_terminal_command":
      return {
        tool: "bash",
        input: compact({
          command: stringArg(args, "command", "cmd"),
          cwd: stringArg(args, "workdir", "cwd") ?? directory,
        }),
      }
    case "unified_search":
      return mapSearch(args)
    case "create_new_file":
      return fileMutation("write", "add", args)
    case "edit_existing_file":
      return {
        tool: "edit",
        input: compact({
          filePath: stringArg(args, "filePath", "filepath", "file"),
          oldString: stringArg(args, "oldString", "searchContent", "search"),
          newString: stringArg(args, "newString", "replaceContent", "replace"),
        }),
        metadata: filesMetadata("update", stringArg(args, "filePath", "filepath", "file")),
      }
    case "todo_write":
      return mapTodoWrite(args) ?? legacy(rawToolName, args)
    case "multi_edit":
    case "delete_file":
    case "rename_file":
    case "spawn_subagent_task":
    case "create_new_skill":
    case "load_skill":
    case "lsp_tool":
      return legacy(rawToolName, args)
    default:
      return legacy(rawToolName, args)
  }
}

function mapSearch(args: Record<string, unknown>): ToolMapping {
  const mode = String(args.type ?? args.mode ?? args.searchType ?? "").toLowerCase()
  const pattern = stringArg(args, "query", "pattern", "search") ?? ""
  const filePath = stringArg(args, "path", "dirPath", "directory", "filePath")
  if (mode === "filename" || mode === "file" || mode === "glob") return { tool: "glob", input: compact({ pattern, path: filePath }) }
  if (mode === "content" || mode === "text" || mode === "grep") return { tool: "grep", input: compact({ pattern, path: filePath }) }
  return legacy("unified_search", args)
}

function fileMutation(tool: "write" | "edit" | "apply_patch", kind: "add" | "update", args: Record<string, unknown>): ToolMapping {
  const filePath = stringArg(args, "filePath", "filepath", "file")
  return {
    tool,
    input: compact({ filePath, content: stringArg(args, "content", "contents") }),
    metadata: filesMetadata(kind, filePath),
  }
}

function mapTodoWrite(args: Record<string, unknown>): ToolMapping | undefined {
  const todos = Array.isArray(args.todos) ? args.todos : parseMarkdownTodos(stringArg(args, "todo", "todos", "content", "markdown"))
  if (!todos || todos.length === 0) return undefined
  return {
    tool: "todowrite",
    input: { todos },
  }
}

function parseMarkdownTodos(text: string | undefined): Array<Record<string, unknown>> | undefined {
  if (!text) return undefined
  const todos: Array<Record<string, unknown>> = []
  for (const line of text.split(/\r?\n/)) {
    const match = /^\s*-\s*\[([ x+\-.])\]\s+(.+?)\s*$/.exec(line)
    if (!match) continue
    todos.push({
      content: match[2],
      status: todoStatus(match[1]),
      priority: "medium",
    })
  }
  return todos.length > 0 ? todos : undefined
}

function todoStatus(marker: string) {
  if (marker === "x" || marker === "+") return "completed"
  if (marker === ".") return "in_progress"
  if (marker === "-") return "cancelled"
  return "pending"
}

function questionParts(input: LegacyToolMapInput): MessageV2.Part[] {
  const args = parsedArgs(input.toolCall)
  const callID = toolCallID(input.toolCall)
  const questions = normalizeQuestions(args)
  if (questions.length === 0) return [toolPart({ ...input, toolCall: { ...input.toolCall, tool: "legacy_ask_user_question" } })]
  const base = {
    sessionID: input.sessionID,
    messageID: input.messageID,
  }
  const answers = normalizeAnswers(input.output ?? input.toolCall.output)
  const question: MessageV2.QuestionPart = {
    ...base,
    id: input.partID,
    type: "question",
    toolCallId: callID,
    questions,
    status: terminalStatus(input.toolCall.status ?? "done") === "completed" ? "completed" : "error",
    response: {
      answers,
    },
  }
  const summary: MessageV2.QuestionSummaryPart = {
    ...base,
    id: `${input.partID}_summary` as MessageV2.QuestionSummaryPart["id"],
    type: "question-summary",
    toolCallId: callID,
    questions,
    answers,
  }
  return [question, summary]
}

function normalizeQuestions(args: Record<string, unknown>): MessageV2.QuestionPart["questions"] {
  const source = Array.isArray(args.questions) ? args.questions : [args]
  return source.flatMap((item) => {
    const data = asRecord(item)
    const question = stringArg(data, "question", "prompt", "text")
    if (!question) return []
    const options = Array.isArray(data.options) ? data.options : []
    return [
      {
        question,
        header: stringArg(data, "header", "title") ?? "Question",
        options: options.flatMap((option) => {
          const opt = asRecord(option)
          const label = stringArg(opt, "label", "value", "text")
          if (!label) return []
          return [{ label, description: stringArg(opt, "description") ?? "" }]
        }),
        ...(data.multiSelect === true || data.multiple === true ? { multiple: true } : {}),
        ...(data.allowOther === true || data.custom === true ? { custom: true } : {}),
      },
    ]
  }) as MessageV2.QuestionPart["questions"]
}

function normalizeAnswers(value: unknown): MessageV2.QuestionSummaryPart["answers"] {
  const parsed = parseJsonish(value)
  if (Array.isArray(parsed)) {
    if (parsed.every((item) => typeof item === "string")) return [parsed as string[]]
    return parsed.flatMap((item) => {
      if (Array.isArray(item)) return [item.filter((answer) => typeof answer === "string") as string[]]
      const answer = normalizeAnswerItem(item)
      // undefined means "not an answer item"; [] means "valid slot, no answer/skipped" and must be preserved.
      return answer ? [answer] : []
    })
  }
  const data = asRecord(parsed)
  if (Array.isArray(data.answers)) return normalizeAnswers(data.answers)
  if (typeof data.answer === "string") return [[data.answer]]
  if (typeof parsed === "string" && parsed.trim()) return [[parsed.trim()]]
  return []
}

function normalizeAnswerItem(value: unknown): string[] | undefined {
  const data = asRecord(value)
  if (Object.keys(data).length === 0) return undefined
  if (data.skipped === true) return []

  const answers = stringArray(data.answers)
  const selectedOptionLabels = stringArray(data.selectedOptionLabels)
  const answer = typeof data.answer === "string" && data.answer.length > 0 ? [data.answer] : []
  const otherText = typeof data.otherText === "string" && data.otherText.trim().length > 0 ? [data.otherText.trim()] : []
  const result = [...(answers ?? []), ...(selectedOptionLabels ?? []), ...answer, ...otherText]
  return result.length > 0 ? result : []
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter((item) => typeof item === "string")
}

function terminalStatus(status: string): LegacyStatus {
  const normalized = status.toLowerCase()
  if (completedStatuses.has(normalized) || approvedStatuses.has(normalized)) return "completed"
  return "error"
}

function isCompletedStatus(status: string) {
  const normalized = status.toLowerCase()
  return completedStatuses.has(normalized) || approvedStatuses.has(normalized)
}

function syntheticCompletedOutput(status: string) {
  return status === "approved" ? "[Legacy tool was approved but no execution result was recorded]" : "[Legacy tool produced no output]"
}

function errorText(status: string, value: unknown) {
  const output = stringifyToolOutput(value, "")
  if (output) return output
  if (unfinishedStatuses.has(status.toLowerCase())) return `Legacy tool did not finish before migration: ${status}`
  return `Legacy tool ended with status: ${status}`
}

function filesMetadata(type: "add" | "update" | "delete" | "move", filePath: string | undefined) {
  return filePath ? { files: [{ type, filePath }] } : undefined
}

function legacy(rawToolName: string, args: Record<string, unknown>): ToolMapping {
  return {
    tool: `legacy_${sanitizeToolName(rawToolName)}`,
    input: args,
  }
}

function parsedArgs(toolCall: LegacyToolCall): Record<string, unknown> {
  for (const candidate of [
    toolCall.parsedArgs,
    toolCall.args,
    toolCall.input,
    toolCall.parameters,
    toolCall.toolCall?.function?.arguments,
  ]) {
    const record = asRecord(candidate)
    if (Object.keys(record).length > 0) return record
  }
  return {}
}

function toolName(toolCall: LegacyToolCall) {
  const raw = toolCall.toolCall?.function?.name ?? toolCall.tool ?? toolCall.name ?? toolCall.toolName
  return typeof raw === "string" && raw.trim() ? raw.trim() : "unknown_tool"
}

function toolCallID(toolCall: LegacyToolCall) {
  const raw = toolCall.id ?? toolCall.callID ?? toolCall.toolCallId ?? toolCall.toolCall?.id
  return typeof raw === "string" && raw.trim() ? raw.trim() : `legacy_${sanitizeToolName(toolName(toolCall))}`
}

function stringArg(args: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = args[key]
    if (typeof value === "string" && value.length > 0) return value
  }
  return undefined
}

function compact(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined))
}

function title(tool: string, input: Record<string, unknown>) {
  const target = typeof input.filePath === "string" ? input.filePath : typeof input.command === "string" ? input.command : tool
  return target
}

function time(value: unknown, fallback: number) {
  if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.floor(value))
  if (typeof value === "string") {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return Math.max(0, Math.floor(fallback))
}
